import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Subject, Observable } from 'rxjs';

import { EditorProject } from '../../super-editor/core/models/editor-project.model';
import { buildEditorRenderJob } from '../../super-editor/render/editor-project-render-job.adapter';

import { ReviewIntakeApiService } from '../../Review/services/review-intake-api.service';
import { ReviewBrowserRenderResult } from '../../Review/models/review-browser-render.model';

import { BackgroundRenderClientService } from '../../../core/render-runtime/worker/background-render-client.service';
import { RenderStatusService } from '../../../core/render-runtime/render-status.service';
import { MediaCacheService } from '../../super-editor/media/media-cache.service';
import { WebavCanvasService } from '../../super-editor/media/webav-canvas.service';
import { prepareEditorAssetForWorker } from '../../super-editor/render/editor-worker-source.util';

import { JivuCommandProgressReporter } from '../../JivuCommand/services/jivu-command-progress-reporter.service';
import { JivuCommandPollerService } from '../../JivuCommand/services/jivu-command-poller.service';
import { JivuCommandService } from '../../JivuCommand/services/jivu-command.service';

export interface ReviewEditorCommandRenderOptions {
  showStatusOverlay?: boolean;
}

/**
 * Root-level Review-editor command render driver.
 *
 * Wave 2b of the render refactor: relocates the page-bound ORCHESTRATION (render → reserve →
 * upload → save → complete/fail the Jivu Command) for the `review_render_or_finalize` flow OUT of
 * the page-hosted `SuperEditorComponent.exportProject()` (which is destroyed when the user
 * navigates to the Command Center mid-render) and INTO this `providedIn: 'root'` singleton.
 * Because the service lives at the application root, a Review-editor command keeps rendering,
 * uploading, saving, and completing even after the editor / Review page is gone.
 *
 * STRICTLY ADDITIVE. This REPLICATES the exact chain `exportProject()`'s internal (Review save)
 * branch runs, REUSING the same shared pieces with no duplication of their internals:
 *   - `buildEditorRenderJob(project)`            — pure EditorProject → RenderJob adapter
 *   - `BackgroundRenderClientService.start(...)` — the one background render worker client
 *   - `ReviewIntakeApiService`                   — reserve (mints the R2 key server-side, layout
 *                                                  FROZEN) + upload + save
 * The non-command (live editing) page path still calls `editor.exportProject()` unchanged; only
 * the command case delegates here. The assets-safe `EditorProject` is handed in by the page (the
 * command payload snapshot, whose assets were already R2-resolved by
 * `prepareReviewCommandEditorProject`).
 */
@Injectable({ providedIn: 'root' })
export class ReviewEditorCommandRenderService {
  private readonly backgroundRender = inject(BackgroundRenderClientService);
  private readonly reviewIntake = inject(ReviewIntakeApiService);
  private readonly renderStatus = inject(RenderStatusService);
  private readonly progressReporter = inject(JivuCommandProgressReporter);
  private readonly command = inject(JivuCommandService);
  private readonly poller = inject(JivuCommandPollerService);
  private readonly mediaCache = inject(MediaCacheService);
  private readonly webavCanvas = inject(WebavCanvasService);

  /**
   * Command ids currently being driven — guards against double-drive (e.g. a re-entrant export) AND
   * tracks what is cancellable. `cancel()` is the background-worker handle's `.cancel`, populated once
   * the render starts; `aborted` is the flag the drive checks at each await boundary so a cancel that
   * lands after the render finishes still skips completion + R2 save.
   */
  private readonly inFlight = new Map<string, { cancel: (() => void) | null; aborted: boolean }>();

  /**
   * Emits after a Review-editor command has rendered, saved the new video, and completed the command.
   * The Review page subscribes to this to reload its list so the freshly-rendered video shows WITHOUT
   * a manual refresh (the page may have been navigated away from during the render, so it can't rely
   * on its own completion handler running). Carries the review item id that was just re-rendered.
   */
  private readonly completedSubject = new Subject<{ commandId: string; reviewItemId: string }>();
  readonly completed$: Observable<{ commandId: string; reviewItemId: string }> = this.completedSubject.asObservable();

  /**
   * Drive a Review-editor command end-to-end: render the EditorProject with the background worker,
   * reserve a processed-output R2 target, upload the rendered video + thumbnail, fan that immutable
   * output to Social while retaining the editable Review source, mirror render progress into the
   * command as a 55→99 band, then complete or fail the command. Survives editor / route teardown
   * because this service is root-scoped.
   */
  async drive(
    commandId: string,
    project: EditorProject,
    reviewItemId: string,
    options: ReviewEditorCommandRenderOptions = {}
  ): Promise<void> {
    const id = (commandId || '').trim();
    const reviewId = (reviewItemId || '').trim();
    if (!id || this.inFlight.has(id)) return;
    if (!project || !reviewId) {
      await this.failCommand(id, 'Review editor command is missing the editor project or review item.');
      return;
    }
    const entry = { cancel: null as (() => void) | null, aborted: false };
    this.inFlight.set(id, entry);
    const showStatusOverlay = options.showStatusOverlay !== false;

    if (showStatusOverlay) {
      this.renderStatus.begin({
        phase: 'rendering',
        progress: 0,
        title: 'Rendering video...',
        sub: 'Encoding in the background',
        encoder: 'background-worker'
      });
    }

    try {
      if (!this.backgroundRender.supported()) {
        throw new Error('This browser cannot render video (WebCodecs/Worker unavailable).');
      }

      // ── Background-worker render (same adapter + client exportProject uses) ──────────────────
      const renderProject = await this.prepareWorkerReadableProject(project);
      const job = buildEditorRenderJob(renderProject);
      const handle = this.backgroundRender.start(job, p => {
        const phase = p.phase === 'encoding' || p.phase === 'rendering' ? 'rendering' : 'preparing';
        const progress01 = Math.max(0, Math.min(1, p.progress || 0));
        if (showStatusOverlay) {
          this.renderStatus.update({
            phase,
            progress: progress01,
            sub: p.message || (phase === 'rendering' ? 'Encoding in the background' : 'Preparing video...')
          });
        }
        // Forward encode progress into the command % as a 55→99 band (the root service now owns
        // progress reporting; the page no longer wires editor.renderProgressSink for commands).
        this.progressReporter.report(id, 55 + Math.round(progress01 * 44), p.message);
      });
      // Make the worker abortable: store its .cancel so `cancel(commandId)` can stop the encode.
      entry.cancel = handle.cancel;
      const out = await handle.result;

      // Cancelled mid/after-render: the cancel path already marked the command cancelled — bail
      // WITHOUT reserving R2, uploading, saving, or completing.
      if (entry.aborted) {
        if (showStatusOverlay) {
          this.renderStatus.done();
        }
        return;
      }

      if (showStatusOverlay) {
        this.renderStatus.done();
      }

      const outputBlob = out.blob;
      const thumbnailBlob = out.thumbnails?.[0] ?? null;

      // ── Processed-output pipeline (reserve → upload → Social fan-out) ────────────────────────
      // Name the file by the ACTUAL encoded format, not a hardcoded .mp4 — a browser without MP4
      // MediaRecorder ships WebM bytes that must not be mislabeled.
      const videoExt = (outputBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
      const videoBaseName = (project.exportSettings.fileName || 'render').replace(/\.(mp4|webm|mov)$/i, '');
      const videoFile = new File([outputBlob], `${videoBaseName}.${videoExt}`, { type: outputBlob.type });
      const thumbnailFile = thumbnailBlob
        ? new File([thumbnailBlob], 'thumb.jpg', { type: 'image/jpeg' })
        : null;

      const rendered: ReviewBrowserRenderResult = {
        file: videoFile,
        thumbnailFile,
        durationSeconds: project.durationSeconds,
        width: project.exportSettings.width || 1080,
        height: project.exportSettings.height || 1920,
        mimeType: outputBlob.type,
        clientRuntime: 'browser'
      };

      const reservation = await firstValueFrom(
        this.reviewIntake.reserveCommandRenderedVideo(reviewId, id)
      );

      const uploads = await firstValueFrom(
        this.reviewIntake.uploadRenderedReviewAssets(rendered, reservation, true)
      );

      const distribution = await firstValueFrom(
        this.reviewIntake.sendCommandRenderedVideoToSocial(reviewId, {
          commandId: id,
          videoFileId: uploads.video.filePath,
          videoContentType: uploads.video.contentType,
          thumbnailFileId: uploads.thumbnail?.filePath ?? null,
        })
      );
      if (!distribution.reviewRetained) {
        throw new Error('The rendered Review item was not retained after Social handoff.');
      }

      await firstValueFrom(this.command.complete(
        id,
        {
          action: 'super-editor-render',
          itemId: reviewId,
          title: project.title,
          distributionJobId: distribution.distributionJobId,
          reviewRetained: distribution.reviewRetained,
          frozenVersion: distribution.frozenVersion
        },
        'Review editor render completed and was sent to Social.'
      ));
      this.command.clearCurrentHandoffFor(id);
      this.poller.claimNextQueuedCommandSoon(id);

      // Release only the command's decoded assets. The Review source key and editable snapshot were
      // deliberately untouched, so reopening Review restores the same timeline.
      this.releaseCommandMedia();

      // Tell any live Review page to reload the retained source after the command completes.
      this.completedSubject.next({ commandId: id, reviewItemId: reviewId });
    } catch (err) {
      // A cancel aborts the worker, which rejects the render promise — that is expected teardown,
      // not a failure. The cancel path already marked the command cancelled, so swallow it.
      if (entry.aborted) {
        if (showStatusOverlay) {
          this.renderStatus.done();
        }
        return;
      }
      if (showStatusOverlay) {
        this.renderStatus.fail(this.errorMessage(err));
      }
      await this.failCommand(id, this.errorMessage(err));
    } finally {
      this.inFlight.delete(id);
    }
  }

  /**
   * Abort the in-flight client render for this command (Wave 5). Sets the abort flag (so a render
   * that is already finishing skips the R2 save + complete) and stops the background worker via its
   * stored `.cancel`. The existing cancel cascade (BE job + command record) is handled by the
   * caller; this only kills the LOCAL render. No-op if the command is not being driven here.
   */
  cancel(commandId: string): void {
    const id = (commandId || '').trim();
    const entry = this.inFlight.get(id);
    if (!entry) return;
    entry.aborted = true;
    try {
      entry.cancel?.();
    } catch {
      // Best effort — the worker may already be gone.
    }
    this.progressReporter.clear(id);
    this.renderStatus.done();
    this.command.clearCurrentHandoffFor(id);
    this.inFlight.delete(id);
    this.releaseCommandMedia();
  }

  private async prepareWorkerReadableProject(project: EditorProject): Promise<EditorProject> {
    const assets = await Promise.all((project.assets || []).map(asset =>
      prepareEditorAssetForWorker(
        asset,
        (url, kind) => this.webavCanvas.resolveWorkerSourceUrl(url, kind),
      )
    ));
    return { ...project, assets };
  }

  private async failCommand(commandId: string, error: string): Promise<void> {
    if (!commandId) return;
    this.progressReporter.clear(commandId);
    try {
      await firstValueFrom(this.command.fail(commandId, error));
      this.poller.claimNextQueuedCommandSoon(commandId);
    } catch {
      // Failing the command is best-effort; never throw out of the driver.
    }
    this.command.clearCurrentHandoffFor(commandId);
    this.releaseCommandMedia();
  }

  /**
   * This driver owns the target-device command media session. Its worker has already
   * terminated before complete/fail, so release the authenticated Blob/object URL cache
   * and any decoder state without touching unrelated command lifecycle state.
   */
  private releaseCommandMedia(): void {
    try { this.mediaCache.clearAll(); } catch { /* best-effort command teardown */ }
    try { this.webavCanvas.disposeAll(); } catch { /* best-effort command teardown */ }
  }

  private errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : 'Review editor render failed.';
  }
}
